import * as Preact from "preact"
import Objdict from "objdict"
import {nanoid} from "nanoid"

import App from "@frog/source/models/App.js"
import World from "@frog/source/models/World.js"
import Stage from "@frog/source/models/Stage.js"
import Zone from "@frog/source/models/Zone.js"
import Step from "@frog/source/models/Step.js"
import Random from "@frog/source/utility/Random.js"
import Navigation from "@frog/source/engine/navigating/Navigation.js"

import * as PlotSteps from "@frog/source/data/plot/steps.js"
import * as PlotConditions from "@frog/source/data/plot/conditions.js"
import * as PlotScripts from "@frog/source/data/plot/scripts.js"
import {RollPomp} from "@frog/source/data/games/dice/scripts/Rolling.js"
import HandleStatusEffects from "@frog/source/data/games/dice/scripts/HandleStatusEffects.js"

import LootModal from "@frog/source/data/games/dice/views/LootModal.jsx"
import RollingModal from "@frog/source/data/games/dice/views/RollingModal.jsx"
import LearnToRightClickModal from "@frog/source/data/games/dice/views/LearnToRightClickModal.jsx"
import RollingHud from "@frog/source/data/games/dice/views/hud/RollingHud.jsx"
import TutorialHud from "@frog/source/data/games/dice/views/hud/TutorialHud.jsx"
import FrogModal from "@frog/source/data/games/dice/views/FrogModal.jsx"

import * as DiceFlow from "@frog/source/data/games/dice/scripts/Flow.js"

export default {
    "slug": "ch0",
    "stage": {
        "images": [
            {
                "url": require("../bg.png"),
                "position": {"x": 1.5, "y": 5, "z": 1},
                "width": 16, "height": 9,
                // "screenspace": "camera",
            },
            {
                "url": require("./map_26x21.png"),
                "position": {"x": 0, "y": 0, "z": 2},
                "width": 26, "height": 21,
                "isSquiggling": true,
            }
        ],
        "units": require("./units.json"),
        "world": World.parse({
            "blocks": require("./blocks.json"),
            "spots": require("./spots.json"),
            "encounters": require("./encounters.json"),
            "width": 64,
            "height": 32,
        }),
        "hud": function(stage) {
            return [
                RollingHud(stage),
                TutorialHud(stage),
            ]
        },
        "scripts": [
            {
                "trigger": "turn:0:start",
                "events": [
                    PlotSteps.ReportStartingAnalytics(),
                    PlotSteps.SpawnPlayerParty(),
                    function EquipInitialItems(stage) {
                        return {
                            "deltastages": Object.values(stage.units).map((unit) => {
                                return {
                                    "unit": {
                                        "key": unit.key,
                                        "equippedItem": unit.diceFaces[0],
                                        "primaryItem": unit.diceFaces[0],
                                    }
                                }
                            })
                        }
                    },
                    Step.deltastage({
                        "player": {
                            "phase": "rolling",
                            "zoomout": 0.75,
                        },
                    }),

                    PlotSteps.PlayAudio("Ambience"),
                    PlotSteps.PlayStinger("Stinger:Start"),
                    PlotSteps.ChapterStart([
                        "Level 0", "Dungeon of the Slime Queen"
                    ]),

                    PlotSteps.PlayAudio("Music:1A"),
                    PlotSteps.Wait(500),
                    LearnToRightClickModal(),
                    Step.deltastage({
                        "player": {
                            "phase": "playing",
                            "zoomout": 1,
                        },
                    }),
                ]
            },
            {
                "trigger": "turn:every-odd:start",
                "events": [
                    PlotSteps.PlayStinger("Stinger:EnemyTurn"),
                    {
                        "duration": 1200,
                        "deltastages": [
                            {
                                "dialogue": {
                                    "type": "TurnStart",
                                    "data": {"affiliation": "bad"},
                                }
                            }
                        ]
                    }
                ]
            },
            {
                "trigger": "turn:every-odd:end",
                "events": [
                    HandleStatusEffects(),
                ]
            },
            {
                "condition": (stage) => {
                    return stage.turn.number % 2 == 0
                        && stage.turn.phase == "start"
                },
                "events": [
                    PlotSteps.ResetTutorialFlags(),
                ]
            },
            {
                "condition": (stage) => {
                    return stage.turn.number % 2 == 1
                        && stage.turn.phase == "end"
                        && PlotConditions.areAllAffiliatedUnitsDead(stage, {"affiliation": "bad"}) != true
                },
                "events": [
                    ...RollPomp(),
                ]
            },
            {
                "isWinCondition": true,
                "condition": (stage) => {
                    return stage.currentEncounterKey == 1
                        && PlotConditions.areAllAffiliatedUnitsDead(stage, {"affiliation": "bad"})
                },
                "events": [
                    PlotSteps.PlayAudio("Music:1B"),
                    PlotSteps.HealPlayerParty({"percent": 1}),
                    PlotSteps.HealPlayerParty({"percent": 0.5, "isDead": true}),
                    DiceFlow.MoveToNewEncounter({"nextEncounterKey": 2, "spotTag": "goodspawn"}),
                    ...LootModal(),
                    ...RollPomp(),
                ]
            },
            {
                "isWinCondition": true,
                "condition": (stage) => {
                    return stage.currentEncounterKey == 2
                        && PlotConditions.areAllAffiliatedUnitsDead(stage, {"affiliation": "bad"})
                },
                "events": [
                    PlotSteps.PlayAudio("Music:2"),
                    PlotSteps.HealPlayerParty({"percent": 1}),
                    PlotSteps.HealPlayerParty({"percent": 0.5, "isDead": true}),
                    DiceFlow.MoveToNewEncounter({"nextEncounterKey": 3, "spotTag": "goodspawn"}),
                    ...LootModal(),
                    FrogModal(),
                    ...RollPomp(),
                ]
            },
            {
                "isWinCondition": true,
                "condition": (stage) => {
                    return stage.currentEncounterKey == 3
                        && PlotConditions.areAllAffiliatedUnitsDead(stage, {"affiliation": "bad"})
                },
                "events": [
                    PlotSteps.PlayAudio("Music:1B"),
                    PlotSteps.HealPlayerParty({"percent": 1}),
                    PlotSteps.HealPlayerParty({"percent": 0.5, "isDead": true}),
                    DiceFlow.MoveToNewEncounter({"nextEncounterKey": 4, "spotTag": "goodspawn"}),
                    ...LootModal(),
                    ...RollPomp(),
                ]
            },
            {
                "isWinCondition": true,
                "condition": (stage) => {
                    return stage.currentEncounterKey == 4
                        && PlotConditions.areAllAffiliatedUnitsDead(stage, {"affiliation": "bad"})
                },
                "events": [
                    PlotSteps.PlayAudio("Music:Boss"),
                    PlotSteps.HealPlayerParty({"percent": 1}),
                    PlotSteps.HealPlayerParty({"percent": 0.5, "isDead": true}),
                    DiceFlow.MoveToNewEncounter({"nextEncounterKey": 5, "spotTag": "goodspawn"}),
                    ...LootModal(),
                    ...RollPomp(),
                ]
            },
            {
                "isWinCondition": true,
                "condition": (stage) => {
                    return stage.currentEncounterKey == 5
                        && PlotConditions.areAllAffiliatedUnitsDead(stage, {"affiliation": "bad"})
                },
                "events": [
                    PlotSteps.ReportSuccessAnalytics(),
                    PlotSteps.PlayAudio("Stinger:Success"),
                    PlotSteps.ChapterSuccess(),
                    PlotSteps.TransitionStart({"isUnskippable": true}),
                    PlotSteps.Wait(250, {"isUnskippable": true}),
                    PlotSteps.WaitingModal({"isUnskippable": true, "duration": 2000}),
                    PlotSteps.KillAudio({"label": "ambience"}),
                    PlotSteps.Go("/games/dice"),
                ]
            },
            {

                "condition": function(stage) {
                    return PlotConditions.areAllAffiliatedUnitsDefeated(stage, {"affiliation": stage.player.affiliation})
                },
                "events": [
                    PlotSteps.PlayAudio("Stinger:Failure"),
                    PlotSteps.KillAudio({"label": "ambience"}),
                    PlotSteps.ReportFailureAnalytics(),
                    PlotSteps.ChapterFailure(),
                    PlotSteps.Go("/games/dice/chapters/chX"),
                ]
            },
        ]
    },
}
